import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { REPORT_NOTE_MAX_LENGTH, type ReportReason } from "@givehere/contracts";
import { useDialogFocus } from "@givehere/ui";

export const REPORT_REASON_LABELS: readonly { value: ReportReason; label: string }[] = [
  { value: "offensive", label: "Offensive or hateful" },
  { value: "harassment", label: "Harassment" },
  { value: "spam", label: "Spam" },
  { value: "misleading", label: "Misleading" },
  { value: "other", label: "Something else" },
];

export type ReportDialogProps = {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSend: (reason: ReportReason, note: string) => void;
};

export function ReportDialog({ busy, error, onCancel, onSend }: ReportDialogProps) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const titleId = useId();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="sheet__panel report__panel" ref={panelRef} tabIndex={-1}>
        <h2 id={titleId} className="donor__title">
          Report this
        </h2>
        <p className="muted">
          Tell us what is wrong. The organizers will look at it. Nobody is told who reported.
        </p>
        <fieldset className="report__reasons">
          <legend>Why are you reporting this?</legend>
          {REPORT_REASON_LABELS.map((option) => (
            <label key={option.value} className="report__reason">
              <input
                type="radio"
                name="report-reason"
                value={option.value}
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
        <label className="field">
          <span>Add a note (optional)</span>
          <textarea
            className="report__note"
            rows={3}
            maxLength={REPORT_NOTE_MAX_LENGTH}
            value={note}
            onChange={(changed) => setNote(changed.target.value)}
          />
          <small className="muted">{REPORT_NOTE_MAX_LENGTH - note.length} characters left</small>
        </label>
        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}
        <div className="report__actions">
          <button type="button" className="pay pay--outline" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="pay"
            disabled={busy || reason === null}
            onClick={() => reason !== null && onSend(reason, note)}
          >
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
